/**
 * Google Drive Picker Integration
 * Handles Google Drive file selection using the Google Picker API
 */

import { loadGoogleAPIs, getAccessToken, getGooglePickerAPIKey, getGooglePickerAppId } from '../utils/google-picker-loader.js';

/**
 * Open Google Drive Picker
 * @param {Object} component - The component instance
 * @param {string} provider - Provider name ('google-drive')
 * @param {string} accountIdentifier - Account identifier
 * @param {string} viewType - View type: 'DOCS' (default) or 'FOLDERS'
 * @param {Function} onPickCallback - Optional callback function when file/folder is picked
 * @param {boolean} multiselect - Enable multiselect (default: false)
 * @param {string} query - Optional default search query to pre-populate in the picker
 */
export async function openGoogleDrivePicker(component, provider, accountIdentifier, viewType = 'DOCS', onPickCallback = null, multiselect = false, query = null) {
  try {
    // Load Google APIs
    await loadGoogleAPIs();

    // Get access token
    const accessToken = await getAccessToken(provider, accountIdentifier);

    if (!accessToken) {
      throw new Error('No access token received from server');
    }

    window.logger?.debug('Access token received for picker', {
      hasToken: !!accessToken,
      tokenLength: accessToken.length,
      tokenPrefix: accessToken.substring(0, 20) + '...'
    });

    // Get API key (from authenticated endpoint)
    const apiKey = await getGooglePickerAPIKey();

    if (!apiKey) {
      throw new Error('Google Picker API key is not available');
    }

    // Get app ID (required for drive.file scope)
    const appId = await getGooglePickerAppId();

    if (!appId) {
      throw new Error('Google Cloud project number (app ID) is not available');
    }

    window.logger?.info('Opening Google Drive picker', {
      hasAccessToken: !!accessToken,
      hasApiKey: !!apiKey,
      hasAppId: !!appId,
      apiKeyLength: apiKey?.length,
      origin: window.location.origin
    });

    // Scroll to top to ensure picker appears at top of viewport
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Create picker with callback that has access to account info
    const accountInfo = component.pendingPickerAccount;

    // Store access token in closure for use in callback
    // Capture it directly so it's available even if component.pendingPickerAccessToken is cleared
    const pickerAccessToken = accessToken;
    component.pendingPickerAccessToken = accessToken;

    // Create views based on viewType
    let view;
    if (viewType === 'FOLDERS') {
      // For folders, use FOLDERS view and enable folder selection
      view = new google.picker.DocsView(google.picker.ViewId.FOLDERS)
        .setMode(google.picker.DocsViewMode.LIST)
        .setOwnedByMe(true)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true);
    } else {
      // Default to DOCS view (files) - enable folder browsing
      view = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setMode(google.picker.DocsViewMode.LIST)
        .setOwnedByMe(true)
        .setIncludeFolders(true); // Enable folder browsing so users can navigate into folders
    }

    // Set default search query if provided
    if (query) {
      view.setQuery(query);
    }

    const pickerBuilder = new google.picker.PickerBuilder()
      .setOAuthToken(accessToken)
      .setDeveloperKey(apiKey)
      .setAppId(appId) // Required for drive.file scope
      .addView(view);

    // Enable multiselect if requested
    if (multiselect) {
      pickerBuilder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
    }

    const picker = pickerBuilder.setCallback((data) => {
      // If custom callback provided, use it
      if (onPickCallback) {
        try {
          onPickCallback(data);
        } catch (error) {
          window.logger?.error('Error in custom picker callback:', error);
        }
        return;
      }

      // Otherwise use default callback handling
      // Temporarily restore currentAccount and accessToken for callback
      const previousAccount = component.currentAccount;
      component.currentAccount = accountInfo;
      // Use the captured token from closure, not component.pendingPickerAccessToken
      component.pendingPickerAccessToken = pickerAccessToken;
      try {
        handlePickerCallback(component, data);
      } finally {
        // Restore previous state (likely null)
        component.currentAccount = previousAccount;
        // Clear pending account and token after callback
        component.pendingPickerAccount = null;
        component.pendingPickerAccessToken = null;
      }
    })
      //.setOrigin(window.location.origin)
      .build();

    picker.setVisible(true);

    // Ensure Google Picker overlay appears above other elements
    // Google Picker creates its own overlay, but we need to ensure it's above the sidebar (z-index 9999)
    const ensurePickerOnTop = () => {
      // Try multiple selectors to find the picker overlay
      const selectors = [
        '[role="dialog"]',
        '.picker-dialog',
        'div[style*="z-index"]', // Any div with inline z-index (Google Picker often uses this)
        'iframe[src*="docs.google.com"]',
        'iframe[src*="picker"]'
      ];

      let pickerOverlay = null;
      for (const selector of selectors) {
        const elements = document.querySelectorAll(selector);
        for (const el of elements) {
          // Check if this looks like a Google Picker element
          const style = window.getComputedStyle(el);
          if (style.position === 'fixed' || style.position === 'absolute') {
            // Check if it's likely the picker (has high z-index or contains picker-related content)
            const zIndex = parseInt(style.zIndex) || 0;
            if (zIndex > 1000 || el.querySelector('iframe[src*="docs.google.com"]') ||
                el.querySelector('iframe[src*="picker"]')) {
              pickerOverlay = el;
              break;
            }
          }
        }
        if (pickerOverlay) break;
      }

      // Also check for iframes and their containers
      if (!pickerOverlay) {
        const iframes = document.querySelectorAll('iframe[src*="docs.google.com"], iframe[src*="picker"]');
        for (const iframe of iframes) {
          const container = iframe.closest('div');
          if (container) {
            pickerOverlay = container;
            break;
          }
        }
      }

      if (pickerOverlay) {
        // Set a very high z-index to ensure it's above everything (sidebar is 9999)
        pickerOverlay.style.zIndex = '999999';
        pickerOverlay.style.position = 'fixed'; // Ensure it's positioned correctly

        // Also ensure all parent elements have proper z-index
        let parent = pickerOverlay.parentElement;
        while (parent && parent !== document.body) {
          const parentStyle = window.getComputedStyle(parent);
          if (parentStyle.position === 'fixed' || parentStyle.position === 'absolute') {
            const currentZIndex = parseInt(parentStyle.zIndex) || 0;
            if (currentZIndex < 999999) {
              parent.style.zIndex = '999999';
            }
          }
          parent = parent.parentElement;
        }
        return true;
      }
      return false;
    };

    // Try immediately, then with increasing delays
    let attempts = 0;
    const maxAttempts = 20;
    const checkInterval = setInterval(() => {
      attempts++;
      if (ensurePickerOnTop() || attempts >= maxAttempts) {
        clearInterval(checkInterval);
      }
    }, 50); // Check every 50ms for up to 1 second

    // Also use MutationObserver to catch dynamically added elements
    const observer = new MutationObserver(() => {
      if (ensurePickerOnTop()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Clean up observer after 5 seconds
    setTimeout(() => {
      observer.disconnect();
      clearInterval(checkInterval);
    }, 5000);
    // Reset guard after picker is shown
    component.isOpeningPicker = false;
  } catch (error) {
    window.logger?.error('Error opening Google Drive picker: ' + error);
    component.showError('Failed to open Google Drive picker. Please try again.');
    // Reset guard on error
    component.isOpeningPicker = false;
  }
}

/**
 * Handle Google Drive Picker callback
 * @param {Object} component - The MediaSourcePicker component instance
 * @param {Object} data - Picker callback data
 */
export async function handlePickerCallback(component, data) {
  if (data.action === google.picker.Action.PICKED) {
    const files = data.docs.map(doc => {
      // Log warning if file doesn't have isShared property or if it's true
      if (doc.isShared === true) {
        window.logger?.warn('WARNING: File selected has isShared=true. This file may not be accessible with drive.file scope!', {
          fileId: doc.id,
          fileName: doc.name,
          mimeType: doc.mimeType,
          serviceId: doc.serviceId
        });
      } else if (doc.isShared === undefined) {
        window.logger?.warn('WARNING: File selected does not have isShared property. Cannot verify if file is shared.', {
          fileId: doc.id,
          fileName: doc.name,
          mimeType: doc.mimeType,
          serviceId: doc.serviceId
        });
      }

      // Extract file ID - try doc.id first, fallback to extracting from doc.url if needed
      let fileId = doc.id;
      if (!fileId && doc.url) {
        // Extract file ID from Google Drive URL if doc.id is not available
        const match = doc.url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
        if (match) {
          fileId = match[1];
        }
      }

      if (!fileId) {
        window.logger?.error('Could not extract file ID from picker doc:', doc);
        throw new Error('Invalid file selection: no file ID found');
      }

      // Check if file is shared (might not be accessible with drive.file scope)
      const isShared = doc.isShared === true;
      if (isShared) {
        // Show warning to user about shared files
        component.showError('⚠️ Warning: This file is shared with you. Files shared with you may not be accessible with the current permissions. Please select files you own, or make a copy of the shared file to your Drive.');
        window.logger?.warn('Selected file is shared (not owned by user). With drive.file scope, shared files may not be accessible.', {
          fileId,
          fileName: doc.name,
          mimeType: doc.mimeType
        });
      }

      return {
        provider: component.currentAccount.provider,
        accountId: component.currentAccount.accountIdentifier,
        fileId: fileId,
        fileUrl: `oauth://${component.currentAccount.provider}/${component.currentAccount.accountIdentifier}/${fileId}`,
        fileName: doc.name || 'File',
        isShared: isShared,
        mimeType: doc.mimeType,
        embedUrl: doc.embedUrl // Store embedUrl for special file types like My Maps
      };
    });

    // For now, dispatch files directly for all providers (including Google Drive)
    // This allows us to test if appId fixes backend access without copying
    window.logger?.info('Dispatching files directly (copying disabled for testing)', {
      provider: component.currentAccount?.provider,
      fileCount: files.length
    });
    component.dispatchEvent(new CustomEvent('files-selected', {
      detail: {
        files,
        textarea: component._targetTextarea || null
      },
      bubbles: true
    }));

    // Close picker modal
    component.close();
  } else if (data.action === google.picker.Action.CANCEL) {
    // User cancelled picker - close modal
    component.close();
  }
}

/**
 * Find or create the FunOldTimes app folder
 * @param {string} accessToken - OAuth access token
 * @returns {Promise<string>} Folder ID
 */
export async function findOrCreateAppFolder(accessToken) {
  const folderName = 'FunOldTimes';
  const apiBaseUrl = 'https://www.googleapis.com/drive/v3';

  try {
    // First, try to find existing folder
    const searchParams = new URLSearchParams({
      q: `name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
      fields: 'files(id,name)',
      pageSize: '1'
    });

    const searchUrl = `${apiBaseUrl}/files?${searchParams.toString()}`;
    const searchResponse = await fetch(searchUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (searchResponse.ok) {
      const searchData = await searchResponse.json();
      if (searchData.files && searchData.files.length > 0) {
        window.logger?.debug('Found existing FunOldTimes folder', { folderId: searchData.files[0].id });
        return searchData.files[0].id;
      }
    }

    // Folder doesn't exist, create it
    window.logger?.debug('Creating FunOldTimes folder');
    const createResponse = await fetch(`${apiBaseUrl}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: folderName,
        mimeType: 'application/vnd.google-apps.folder'
      })
    });

    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      throw new Error(`Failed to create folder: ${createResponse.status} ${errorText}`);
    }

    const folderData = await createResponse.json();
    window.logger?.info('Created FunOldTimes folder', { folderId: folderData.id });
    return folderData.id;
  } catch (error) {
    window.logger?.error('Error finding or creating FunOldTimes folder:', error);
    throw error;
  }
}

/**
 * Copy a file to the FunOldTimes app folder
 * @param {string} sourceFileId - ID of the file to copy
 * @param {string} accessToken - OAuth access token
 * @param {string} appFolderId - ID of the app folder
 * @param {string} fileName - Optional new name for the copied file
 * @returns {Promise<string>} ID of the copied file
 */
export async function copyFileToAppFolder(sourceFileId, accessToken, appFolderId, fileName = null) {
  const apiBaseUrl = 'https://www.googleapis.com/drive/v3';

  try {
    window.logger?.debug('Copying file to app folder', {
      sourceFileId,
      appFolderId,
      fileName
    });

    // Copy the file to the app folder
    const copyBody = {
      parents: [appFolderId]
    };

    if (fileName) {
      copyBody.name = fileName;
    }

    const copyUrl = `${apiBaseUrl}/files/${sourceFileId}/copy`;
    const copyResponse = await fetch(copyUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(copyBody)
    });

    if (!copyResponse.ok) {
      const errorText = await copyResponse.text();
      window.logger?.error('Failed to copy file to app folder', {
        sourceFileId,
        status: copyResponse.status,
        errorText: errorText.substring(0, 500)
      });
      throw new Error(`Failed to copy file: ${copyResponse.status} ${errorText}`);
    }

    const copiedFile = await copyResponse.json();
    window.logger?.info('File copied to app folder', {
      sourceFileId,
      copiedFileId: copiedFile.id,
      appFolderId
    });

    return copiedFile.id;
  } catch (error) {
    window.logger?.error('Error copying file to app folder:', error);
    throw error;
  }
}

